/**
 * The optional terrain background (FD §4, D9): the prototype's generated elevation bands, display only.
 * Heights come from the plan's seed; the ground is flattened around the entrance like in the prototype.
 */
import { vnoise } from '../../core/raster';
import type { PlanDoc } from '../../shared/types';

/** Height samples per side. */
const TR = 120;
const RAMP: [number, [number, number, number]][] = [[0, [120, 170, 95]], [0.4, [190, 205, 120]], [0.7, [196, 160, 108]], [1, [236, 236, 236]]];

/** Default hilliness (height range in metres) and band height for a newly switched-on background. */
export const TERRAIN_DEFAULTS = { hilliness: 25, band: 3 };

interface Cached { key: string; canvas: HTMLCanvasElement; heights: Float32Array }
let cache: Cached | null = null;

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

function build(doc: PlanDoc, hilliness: number): Float32Array {
  const M = doc.size, raw = new Float32Array(TR * TR), out = new Float32Array(TR * TR);
  let mn = 1e9, mx = -1e9;
  for (let j = 0; j < TR; j++) for (let i = 0; i < TR; i++) {
    let v = 0, amp = 1, tot = 0, f = 1;
    for (let o = 0; o < 4; o++) { v += amp * vnoise(i / TR * M / 220 * f, j / TR * M / 220 * f, doc.seed + o * 101); tot += amp; amp *= 0.5; f *= 2; }
    v /= tot; raw[j * TR + i] = v; mn = Math.min(mn, v); mx = Math.max(mx, v);
  }
  const ent = Object.values(doc.icons).find((ic) => ic.type === 'entrance') ?? { x: M / 2, y: M - 40 };
  const eh = hilliness * 0.4;
  for (let j = 0; j < TR; j++) for (let i = 0; i < TR; i++) {
    const x = i / (TR - 1) * M, y = j / (TR - 1) * M, d = Math.hypot(x - ent.x, y - ent.y), t = clamp((d - 40) / 70, 0, 1), s = t * t * (3 - 2 * t);
    out[j * TR + i] = eh + (((raw[j * TR + i] ?? 0) - mn) / ((mx - mn) || 1) * hilliness - eh) * s;
  }
  return out;
}

function bandRGB(b: number, band: number, hilliness: number): [number, number, number] {
  const t = clamp(b * band / Math.max(hilliness, 1), 0, 1);
  for (let k = 1; k < RAMP.length; k++) {
    const [t1, c1] = RAMP[k]!;
    if (t <= t1) {
      const [t0, c0] = RAMP[k - 1]!, f = (t - t0) / ((t1 - t0) || 1);
      return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f];
    }
  }
  return [236, 236, 236];
}

/** The banded terrain picture (TR×TR pixels, stretched over the map when drawn), or null when switched off. */
export function terrainCanvas(doc: PlanDoc): HTMLCanvasElement | null {
  const t = doc.terrain;
  if (!t || !t.show || typeof document === 'undefined') return null;
  const ent = Object.values(doc.icons).find((ic) => ic.type === 'entrance');
  const key = `${doc.seed}|${doc.size}|${t.hilliness}|${t.band}|${ent ? `${Math.round(ent.x)},${Math.round(ent.y)}` : ''}`;
  if (cache && cache.key === key) return cache.canvas;
  const heights = build(doc, t.hilliness);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = TR;
  const g = canvas.getContext('2d');
  if (!g) return null;
  const img = g.createImageData(TR, TR);
  for (let k = 0; k < TR * TR; k++) {
    const c = bandRGB(Math.floor((heights[k] ?? 0) / Math.max(t.band, 0.5)), t.band, t.hilliness);
    img.data[k * 4] = c[0]; img.data[k * 4 + 1] = c[1]; img.data[k * 4 + 2] = c[2]; img.data[k * 4 + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  cache = { key, canvas, heights };
  return canvas;
}

/** Ground height in metres at a map point, or null when the background is off. */
export function groundHeight(doc: PlanDoc, x: number, y: number): number | null {
  if (!terrainCanvas(doc) || !cache) return null;
  const H = cache.heights, M = doc.size;
  const fx = clamp(x / M * (TR - 1), 0, TR - 1), fy = clamp(y / M * (TR - 1), 0, TR - 1);
  const i = Math.min(TR - 2, Math.floor(fx)), j = Math.min(TR - 2, Math.floor(fy)), u = fx - i, v = fy - j;
  const a = H[j * TR + i] ?? 0, b = H[j * TR + i + 1] ?? 0, c = H[(j + 1) * TR + i] ?? 0, d = H[(j + 1) * TR + i + 1] ?? 0;
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
